import { useQuery } from '@tanstack/react-query';
import type { Loop } from '@/common/api/types/loops';
import { useOrgId } from '@/common/hooks/useOrgId';
import { commonQueryKeys } from '@/common/queryKeys/common';
import { LoopsApi } from '@/features/items/api/loops';

// `item-store`'s loop index caps a page at DEFAULT_PAGE_SIZE_LIMIT (50), and `useLoopsQuery` reads
// only the first page. That is fine where a partial list degrades gracefully, but not where the list
// IS the choice on offer — a picker must not silently omit Loops an org actually has.
//
// The page cap mirrors SirApi's own paged reads: bounded so a pathological cursor loop can't spin
// forever, and high enough that no real org reaches it.
const MAX_PAGES = 25;

const fetchEveryLoop = async (): Promise<Loop[]> => {
  const loops: Loop[] = [];
  let cursor: string | undefined;

  for (let page = 0; page < MAX_PAGES; page++) {
    const response = await LoopsApi.getLoops(cursor ? { cursor } : {});
    loops.push(...response.data);
    // `cursor` on the response is the NEXT page's cursor, absent on the last page.
    if (!response.cursor) break;
    cursor = response.cursor;
  }

  return loops;
};

export const useAllLoopsQuery = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const orgId = useOrgId();
  return useQuery({
    queryKey: commonQueryKeys.loops.everyPage(orgId),
    queryFn: fetchEveryLoop,
    enabled,
  });
};
